import React, { useState, forwardRef } from 'react';
import { Popover, Stack, Grid, Box, IconButton, Tooltip, Typography } from '@mui/material';

const colors = [
  '#FFEB3C',
  '#FFD700',
  '#FF9900',
  '#FF8C00',
  '#FF99FF',
  '#FF66FF',
  '#9C26B0',
  '#8E24AA',
  '#3F51B5',
  '#3949AB',
  '#03A9F4',
  '#009788',
  '#66FFCC',
  '#4CAF50',
  '#4BAF4F',
  '#7E5D4E',
  '#795548',
];

// Small Checkbox root is 9px + 18px icon + 9px padding (SwitchBase).
const COMPACT_COLOR_CONTROL_PX = 36;

const ColorPicker = forwardRef(function ColorPicker(
  { selectedColor, changeColor, compact = false, controlOnly = false, disabled = false },
  ref
) {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'color-picker-popover' : undefined;

  const swatch = (
    <Box
      sx={{
        width: compact ? 18 : 24,
        height: compact ? 18 : 24,
        flexShrink: 0,
        backgroundColor: selectedColor,
        border: '1px solid rgb(204, 204, 204)',
      }}
    />
  );

  const compactControlSx =
    controlOnly && compact
      ? {
          p: 0,
          m: 0,
          width: COMPACT_COLOR_CONTROL_PX,
          minWidth: COMPACT_COLOR_CONTROL_PX,
          maxWidth: COMPACT_COLOR_CONTROL_PX,
          height: COMPACT_COLOR_CONTROL_PX,
          minHeight: COMPACT_COLOR_CONTROL_PX,
          maxHeight: COMPACT_COLOR_CONTROL_PX,
          boxSizing: 'border-box',
          borderRadius: '1px',
          pl: 1.125,
          pr: 1.125,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }
      : compact
        ? {
            p: 0,
            pl: 1.125,
            pr: 0.5,
            py: 0.625,
          }
        : { px: 1 };

  const trigger = (
    <Tooltip title="Choose Color">
      <IconButton
        ref={ref}
        disabled={disabled}
        size={compact ? 'small' : 'medium'}
        sx={{
          borderRadius: '1px',
          ...compactControlSx,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
        }}
        aria-describedby={id}
        variant="contained"
        onClick={handleClick}
      >
        {controlOnly && compact ? (
          swatch
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={compact ? 0.5 : 1}
            useFlexGap
          >
            {swatch}
            <Typography
              variant="body2"
              component="span"
              sx={{
                fontSize: compact ? '0.75rem' : '0.875rem',
                lineHeight: compact ? 1.15 : 1.4375,
                verticalAlign: 'middle',
                color: 'black',
              }}
            >
              Color
            </Typography>
          </Stack>
        )}
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {controlOnly && compact ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {trigger}
        </Box>
      ) : (
        trigger
      )}

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Grid container spacing={1}>
            {colors.map((c) => (
              <Grid item key={c}>
                <IconButton
                  sx={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: c,
                    '&:hover': {
                      border: '2px solid #aaa',
                    },
                  }}
                  data-value={c}
                  onClick={(ev) => {
                    changeColor(ev);
                    handleClose();
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Popover>
    </>
  );
});

export default ColorPicker;